(() => {
  if (customElements.get('gvp-hs-live')) return;
  class GvpHsLive extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.games = [];
      this.slots = new Map();
      this.finished = new Set();
      this.hidden = true;
      this.nodes = Object.fromEntries(['club-score','opponent-score','opponent','clock','location','link'].map(key => [key, this.querySelector('[data-live-' + key + ']')]));
      this.onVisibility = () => this.reconcile();
      document.addEventListener('visibilitychange', this.onVisibility);
      this.reconcileTimer = setInterval(() => this.reconcile(), 30000);
      this.renderTimer = setInterval(() => this.render(), 1000);
      this.loadSchedule();
    }
    async loadSchedule() {
      try {
        const response = await fetch(this.dataset.scheduleUrl, { cache: 'no-cache' });
        if (!response.ok) throw new Error('Schedule unavailable');
        const body = await response.json();
        if (!Array.isArray(body)) throw new Error('Invalid schedule');
        this.games = body.filter(g => /^\d+$/.test(String(g.id)) && Number.isFinite(Date.parse(g.startUtc)) && typeof g.opponent === 'string' && typeof g.home === 'boolean');
        if (this.started) this.reconcile();
      } catch {
        this.hidden = true;
        if (this.started) this.scheduleRetry = setTimeout(() => this.loadSchedule(), 60000);
      }
    }
    eligible(game, now = Date.now()) {
      const start = Date.parse(game.startUtc);
      return now >= start - 45 * 60000 && now <= start + 6 * 3600000;
    }
    reconcile() {
      if (!this.started) return;
      const wanted = document.visibilityState === 'hidden' ? [] : this.games.filter(g => this.eligible(g) && !this.finished.has(String(g.id)));
      const ids = new Set(wanted.map(g => String(g.id)));
      for (const [id, slot] of this.slots) if (!ids.has(id)) this.drop(slot);
      for (const game of wanted) {
        const id = String(game.id);
        if (!this.slots.has(id)) {
          const slot = { id, game, retry: 0, record: null, receivedAt: 0, lastHeard: 0 };
          this.slots.set(id, slot);
          this.open(slot);
        }
      }
      this.render();
    }
    current(slot) { return this.started && this.slots.get(slot.id) === slot; }
    open(slot) {
      if (!this.current(slot) || !this.eligible(slot.game)) return;
      clearTimeout(slot.retryTimer);
      const socket = new WebSocket('wss://sb26.gamesheet.io/scoreboard/' + encodeURIComponent(slot.id));
      slot.socket = socket;
      slot.lastHeard = Date.now();
      slot.record = null;
      let closed = false;
      const fail = () => {
        if (closed) return;
        closed = true;
        clearInterval(slot.heartbeat);
        clearTimeout(slot.firstFrame);
        slot.record = null;
        this.render();
        if (socket.readyState < 2) socket.close();
        if (this.current(slot) && this.eligible(slot.game) && !this.finished.has(slot.id)) {
          slot.retryTimer = setTimeout(() => this.open(slot), Math.min(60000, 10000 * Math.pow(2, slot.retry++)));
        }
      };
      slot.firstFrame = setTimeout(fail, 20000);
      socket.addEventListener('open', () => {
        if (!this.current(slot)) return socket.close();
        slot.heartbeat = setInterval(() => {
          if (Date.now() - slot.lastHeard > 60000) return fail();
          if (socket.readyState === WebSocket.OPEN) {
            socket.send('ping');
            socket.send(JSON.stringify({ type: 'get-state' }));
          }
        }, 25000);
      });
      socket.addEventListener('message', event => {
        if (!this.current(slot) || slot.socket !== socket || closed) return;
        slot.lastHeard = Date.now();
        if (String(event.data) === 'pong') return;
        let message;
        try { message = JSON.parse(String(event.data)); } catch { return; }
        if (!['scoreboard-load','scoreboard-update','scoreboard-end'].includes(message.type)) return;
        clearTimeout(slot.firstFrame);
        slot.retry = 0;
        if (message.type === 'scoreboard-end') { slot.record = null; this.render(); return; }
        const record = message.data;
        if (!record || String(record.gameId) !== slot.id) {
          slot.record = null;
          return this.render();
        }
        if (record.status === 'final') return this.finish(slot);
        const score = record.computed?.final;
        const valid = score && Number.isInteger(score.home) && score.home >= 0 && Number.isInteger(score.visitor) && score.visitor >= 0;
        slot.record = record.status === 'in_progress' && valid ? record : null;
        slot.receivedAt = Date.now();
        this.render();
      });
      socket.addEventListener('close', fail);
      socket.addEventListener('error', fail);
    }
    finish(slot) {
      this.finished.add(slot.id);
      this.drop(slot);
      this.render();
    }
    drop(slot) {
      this.slots.delete(slot.id);
      clearTimeout(slot.retryTimer);
      clearTimeout(slot.firstFrame);
      clearInterval(slot.heartbeat);
      slot.record = null;
      if (slot.socket && slot.socket.readyState < 2) slot.socket.close();
    }
    write(node, value) {
      const text = String(value);
      if (node && node.textContent !== text) node.textContent = text;
    }
    render() {
      if (!this.started) return;
      const now = Date.now();
      const active = [...this.slots.values()].filter(s => s.record && this.eligible(s.game, now) && now - s.lastHeard <= 60000 && s.socket?.readyState === WebSocket.OPEN).sort((a,b) => Date.parse(a.game.startUtc) - Date.parse(b.game.startUtc))[0];
      if (!active || document.visibilityState === 'hidden') { this.hidden = true; return; }
      const score = active.record.computed.final;
      this.write(this.nodes['club-score'], active.game.home ? score.home : score.visitor);
      this.write(this.nodes['opponent-score'], active.game.home ? score.visitor : score.home);
      this.write(this.nodes.opponent, active.game.opponent);
      this.write(this.nodes.location, active.game.location);
      this.nodes.link.href = 'https://gamesheetstats.com/seasons/15154/games/' + encodeURIComponent(active.id);
      const clock = active.record.clock;
      let text = '';
      if (clock && typeof clock.periodLabel === 'string') {
        text = clock.periodLabel;
        if (Number.isFinite(clock.remainingSec)) {
          const elapsed = clock.running ? Math.floor((now - active.receivedAt) / 1000) : 0;
          const seconds = Math.max(0, Math.floor(clock.remainingSec) - elapsed);
          text += ' · ' + Math.floor(seconds / 60) + ':' + String(seconds % 60).padStart(2,'0');
        }
      }
      this.write(this.nodes.clock, text);
      this.hidden = false;
    }
    disconnectedCallback() {
      this.started = false;
      clearTimeout(this.scheduleRetry);
      clearInterval(this.reconcileTimer);
      clearInterval(this.renderTimer);
      document.removeEventListener('visibilitychange', this.onVisibility);
      for (const slot of this.slots?.values() || []) this.drop(slot);
      this.hidden = true;
    }
  }
  customElements.define('gvp-hs-live', GvpHsLive);
})();

